import { Injectable } from '@angular/core';
import { HttpService } from '@api/http/http.service';
import {
  EVENTS_STREAMING_ROUTE,
  VIDEOS_ROUTE_NEW,
  VIDEOS_TAB_ROUTE,
} from '@api/http/http-routes';
import {
  DefaultResponse,
  IDefaultListResponse,
  IDefaultResponse,
} from '@api/models/default-response/response';
import { IVideoFeedEntry } from '@api/models/video/video-feed-entry';
import { Event } from '@api/models/event/event';
import {
  IVideoUploadData,
  VideoUploadTarget,
} from '@api/models/video/video-upload-data';
import { Observable } from 'rxjs';
import { StreamRequestStatus } from '@api/models/video/stream-request-status';
import { SportType } from '@lib/models/sport-type/sport-type';
import { SportTypeService } from '@api/services/sport-type/sport-type.service';
import { VideoSubType } from '../../models/video/video';
import { EntityType } from '@lib/models/entity/entity-type';

type VideoFeedEntriesResponse = IDefaultListResponse<IVideoFeedEntry>;

@Injectable({
  providedIn: 'root',
})
export class VideosHttpService {
  constructor(protected http: HttpService) {}

  public async setGcoreVideoForEntity(
    sport: SportType,
    entity_type: EntityType,
    entityid: string,
    gcoreid: number,
    video_subtype: VideoSubType
  ): Promise<DefaultResponse> {
    return this.http
      .post<DefaultResponse>([VIDEOS_ROUTE_NEW, 'set-content/gcore'], {
        sport,
        entity_type,
        entityid,
        gcoreid,
        video_subtype,
      })
      .toPromise();
  }

  public async postOpenViewingSession(): Promise<DefaultResponse> {
    return this.http
      .post<DefaultResponse>(['viewing-sessions', 'open'])
      .toPromise();
  }

  public async getLatestNewsVideos(
    skip = 0,
    limit = 0
  ): Promise<VideoFeedEntriesResponse> {
    return this.http.get<VideoFeedEntriesResponse>([
      VIDEOS_ROUTE_NEW,
      'news/latest',
      skip,
      limit,
    ]);
  }

  public async getPopularLatestNewsVideos(
    skip = 0,
    limit = 0
  ): Promise<VideoFeedEntriesResponse> {
    return this.http.get<VideoFeedEntriesResponse>(
      [VIDEOS_ROUTE_NEW, 'news/latest', skip, limit],
      ['popular=true']
    );
  }

  public async getPopularVideos(
    status = StreamRequestStatus.Replay,
    skip = 0,
    limit = 0,
    sportType = SportTypeService.selectedSportType
  ): Promise<Event[]> {
    const route =
      sportType === SportType.Handball
        ? 'handball/events/streaming'
        : EVENTS_STREAMING_ROUTE;
    const response = await this.http.get<IDefaultListResponse<Event>>([
      route,
      'popular',
      status,
      skip,
      limit,
    ]);
    return response.data;
  }

  public async getVideoFeedEntries(
    skip = 0,
    limit = 0
  ): Promise<VideoFeedEntriesResponse> {
    return this.http.get<VideoFeedEntriesResponse>([
      VIDEOS_TAB_ROUTE,
      skip,
      limit,
    ]);
  }

  public async getVideosTargetType(
    id: string,
    type: string,
    skip = 0,
    limit = 0
  ): Promise<VideoFeedEntriesResponse> {
    const queryParams = [`skip=${skip}`, `limit=${limit}`];
    return this.http.get<VideoFeedEntriesResponse>(
      [VIDEOS_ROUTE_NEW, type, id],
      queryParams
    );
  }

  public async getVideosForTeam(
    teamId: string,
    skip = 0,
    limit = 0
  ): Promise<VideoFeedEntriesResponse> {
    const queryParams = [`skip=${skip}`, `limit=${limit}`];
    return this.http.get<VideoFeedEntriesResponse>(
      [VIDEOS_ROUTE_NEW, 'team', teamId],
      queryParams
    );
  }

  public async getVideosForLeague(
    leagueId: string,
    skip = 0,
    limit = 0
  ): Promise<VideoFeedEntriesResponse> {
    const queryParams = [`skip=${skip}`, `limit=${limit}`];
    return this.http.get<VideoFeedEntriesResponse>(
      [VIDEOS_ROUTE_NEW, 'league', leagueId],
      queryParams
    );
  }

  public deleteVideo(videoId: string): Promise<IDefaultResponse> {
    return this.http.delete([VIDEOS_ROUTE_NEW, videoId]).toPromise();
  }

  public postUploadVideo(data: IVideoUploadData): Observable<any> {
    const formData = new FormData();
    formData.append('video', data.file);
    formData.append('target_type', data.target_type);
    formData.append('objectid', data.objectid);
    if (data.subtype) {
      formData.append('subtype', data.subtype);
    }
    return this.http.post<any>([VIDEOS_ROUTE_NEW, 'upload'], formData, true);
  }

  public putVideoThumbnail(
    videoId: string,
    objectId: string,
    file: Blob,
    target: VideoUploadTarget
  ): Promise<IDefaultResponse> {
    const formData = new FormData();
    formData.append('picture', file);
    formData.append('target_type', target);
    formData.append('objectid', objectId);
    return this.http
      .put<IDefaultResponse>(
        [VIDEOS_ROUTE_NEW, 'setpreview', videoId],
        formData
      )
      .toPromise();
  }
}
